/**
 * Phase 131 — StreamX Alpha Studio (Broadcaster)
 * ---------------------------------------------
 * Two-step flow:
 *   1. Pre-Go-Live form: title, sport, description -> POST /api/livex/streams
 *   2. Live broadcast: LiveKitRoom with ControlBar + share link panel
 *
 * UI doctrine: high-contrast text (no opacity tricks), 16px+ inputs,
 * thick borders, investor-ready polish. Premium dark theme.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer, ControlBar, GridLayout, ParticipantTile, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import {
  Radio, ArrowLeft, Video, Mic, Copy, Share2, AlertTriangle, Loader2,
  StopCircle, CheckCircle2, Link as LinkIcon, ExternalLink, TrendingUp,
} from 'lucide-react';
import api from '@/lib/api';
import '@/styles/livex.css';
import LivexChatPanel from '@/components/livex/LivexChatPanel';
import LivexViewerBadge from '@/components/livex/LivexViewerBadge';
import LivexHighlightPanel from '@/components/livex/LivexHighlightPanel';
import LivexPilotCapModal from '@/components/livex/LivexPilotCapModal';
import LivexStudioCoachmark from '@/components/livex/LivexStudioCoachmark';
import LivexCoachConcierge from '@/components/livex/LivexCoachConcierge';
import { useAuth } from '@/contexts/AuthContext';
// Phase 140A — Broadcast Pro Lite
import BroadcasterScorePanel from '@/components/livex/broadcast/BroadcasterScorePanel';
import StreamHealthBadge from '@/components/livex/broadcast/StreamHealthBadge';
// Phase 140A-replay-fix — Reliable egress kickoff
import EgressKickoff from '@/components/livex/broadcast/EgressKickoff';
// Phase 140B.1 — Audio Enhancement
import BroadcasterAudioPanel from '@/components/livex/broadcast/BroadcasterAudioPanel';
import useAudioEnhancementSettings from '@/hooks/useAudioEnhancementSettings';
// Phase 140B.2 — Manual highlight marker (⭐)
import HighlightMarkerButton from '@/components/livex/broadcast/HighlightMarkerButton';

const SPORTS = ['football', 'cricket', 'badminton', 'basketball', 'tennis', 'hockey', 'volleyball', 'other'];

function BroadcasterGrid() {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  return (
    <GridLayout tracks={tracks} style={{ height: '100%' }}>
      <ParticipantTile />
    </GridLayout>
  );
}

/**
 * Lives inside <LiveKitRoom>. Reads the live participant count, sends a
 * heartbeat every 15s, and surfaces peak-viewers through onPeakChange so the
 * parent can render the side panel + persist peak on stream end.
 */
function BroadcasterTelemetry({ streamId, onCountChange, onPeakChange, currentPeak }) {
  const lastSentRef = useRef({ count: -1, at: 0 });

  const handleCount = (count) => {
    onCountChange?.(count);
    if (count > (currentPeak || 0)) onPeakChange?.(count);

    // Heartbeat: only POST when count changed OR every 15s (whichever first).
    const now = Date.now();
    const elapsed = now - lastSentRef.current.at;
    if (count !== lastSentRef.current.count || elapsed > 15000) {
      lastSentRef.current = { count, at: now };
      api.post(`/livex/streams/${streamId}/heartbeat`, {
        viewer_count: count,
        peak_viewers: Math.max(count, currentPeak || 0),
      }).catch(() => { /* best-effort */ });
    }
  };

  return <LivexViewerBadge isBroadcaster onChange={handleCount} />;
}

export default function LivexStudio() {
  const navigate = useNavigate();
  const { streamId: urlStreamId } = useParams();

  const [stream, setStream] = useState(null);
  const [form, setForm] = useState({ title: '', sport: 'football', description: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [ended, setEnded] = useState(false);
  // Phase 131B — live audience tracking
  const [viewerCount, setViewerCount] = useState(0);
  const [peakViewers, setPeakViewers] = useState(0);

  // Stable callback identity so LivexChatPanel doesn't loop.
  const handleStreamUpdate = (patch) => {
    setStream((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  // If user navigated with a streamId in the URL, load it.
  useEffect(() => {
    if (!urlStreamId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/livex/streams/${urlStreamId}`);
        if (!active) return;
        // Studio needs a broadcaster token. The viewer GET only returns a viewer
        // token; if user lands directly on /livex/studio/:id we treat as fresh.
        // We still surface the existing stream so they can re-end it if needed.
        setStream({ ...data, broadcaster_token: data.viewer_token ? '' : '' });
      } catch {
        // not found -> show creation form
      }
    })();
    return () => { active = false; };
  }, [urlStreamId]);

  const watchUrl = useMemo(() => {
    if (!stream) return '';
    return `${window.location.origin}/livex/watch/${stream.id}`;
  }, [stream]);

  const [capModal, setCapModal] = useState(null); // Phase 134B: celebratory 402 modal payload
  const [activation, setActivation] = useState(null); // Phase 134C: studio coachmark gate
  const { user } = useAuth();

  // Phase 140B.1 — Audio Enhancement settings (persisted in localStorage).
  // The `audioConstraints` are wired into <LiveKitRoom audio={...}> below
  // so noise suppression / echo cancellation / auto-gain are applied at
  // the WebRTC media-capture layer the moment the broadcaster connects.
  const {
    settings: audioSettings,
    setSetting: setAudioSetting,
    audioConstraints,
  } = useAudioEnhancementSettings();

  useEffect(() => {
    // Phase 134C — fetch activation status to decide whether to show the
    // studio coachmark for first-time broadcasters. Silently no-op on error.
    let active = true;
    api.get('/livex/me/activation-status')
      .then((r) => active && setActivation(r.data || null))
      .catch(() => { /* ignore */ });
    return () => { active = false; };
  }, []);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!form.title.trim()) { setError('Stream title is required'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/livex/streams', {
        title: form.title.trim(),
        sport: form.sport,
        description: form.description.trim(),
      });
      setStream(data);
      // Reflect the new stream id in the URL (so refresh keeps state).
      window.history.replaceState(null, '', `/livex/studio/${data.id}`);
    } catch (err) {
      // Phase 134B — celebratory 402 path. Backend returns a structured
      // detail object: {code, title, message, cta_label, cta_url, ...}
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail && typeof detail === 'object' && detail.code === 'pilot_cap_reached') {
        setCapModal(detail);
      } else {
        setError((detail && typeof detail === 'string' ? detail : null) || detail?.message || 'Failed to create stream');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(watchUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {/* ignore */}
  };

  const handleEnd = async () => {
    if (!stream) return;
    if (!window.confirm('End this stream? Viewers will be disconnected.')) return;
    try {
      await api.post(`/livex/streams/${stream.id}/end`, {});
      setEnded(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to end stream');
    }
  };

  // ---------- CREATION FORM ----------
  if (!stream) {
    return (
      <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-studio-create">
        {/* Phase 134B — celebratory pilot-cap modal. Opens automatically when
            POST /livex/streams returns 402 with a structured detail payload. */}
        <LivexPilotCapModal
          open={!!capModal}
          onClose={() => setCapModal(null)}
          hardCapMode
        />
        {/* Phase 134D — Broadcast Coach concierge. Only renders for academy
            owners who haven't activated yet (server-gated via activation.has_first_stream).
            Shows after 90s of idle, dismissible, sticky-dismissed per academy. */}
        {activation?.role === 'academy_owner' && !activation?.has_first_stream && (
          <LivexCoachConcierge
            academyId={user?.academy_id}
            sport={form?.sport}
            streamsCount={0}
            titleFilled={!!(form?.title || '').trim()}
          />
        )}
        <div className="max-w-2xl mx-auto px-6 py-10">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="livex-studio-back"
            className="inline-flex items-center gap-2 text-base font-semibold text-white hover:text-rose-400 transition-colors mb-8"
          >
            <ArrowLeft className="h-5 w-5" /> Back to Dashboard
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-2xl grid place-items-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <Radio className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] font-bold text-[#FF5A1F]">STREAMX · ALPHA</div>
              <h1 className="text-3xl font-black text-white">Start a Live Broadcast</h1>
            </div>
          </div>
          <p className="livex-body-text mb-8">Set up your match, generate a public watch link, and go live in seconds.</p>

          <form onSubmit={handleCreate} className="space-y-6" data-testid="livex-studio-form">
            <div>
              <label className="livex-label">Stream Title <span className="livex-label-required">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. U-14 Final · VBFC vs Bengaluru United"
                maxLength={120}
                data-testid="livex-studio-title"
                className="livex-input"
              />
            </div>

            <div>
              <label className="livex-label">Sport</label>
              <div className="grid grid-cols-4 gap-2">
                {SPORTS.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setForm({ ...form, sport: s })}
                    data-testid={`livex-studio-sport-${s}`}
                    data-active={form.sport === s ? 'true' : 'false'}
                    className="livex-sport"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="livex-label">Description (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Match details, teams, venue..."
                maxLength={500}
                rows={4}
                data-testid="livex-studio-description"
                className="livex-textarea"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-rose-950 border-2 border-rose-700 rounded-lg text-rose-100 text-sm font-bold" data-testid="livex-studio-error">
                <AlertTriangle className="h-4 w-4" /> {error}
              </div>
            )}

            <div className="relative">
              <button
                type="submit"
                disabled={creating}
                data-testid="livex-studio-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-black uppercase tracking-wider text-white rounded-lg border-2 border-[#FF5A1F] transition-all disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', fontSize: '16px' }}
              >
                {creating ? <><Loader2 className="h-5 w-5 animate-spin" /> Creating...</> : <><Radio className="h-5 w-5" /> Create Stream</>}
              </button>
              {/* Phase 134C — first-time broadcaster coachmark. Only renders
                  while the academy hasn't streamed yet (server-gated) AND the
                  user hasn't dismissed it (localStorage). */}
              {activation?.role === 'academy_owner' && !activation?.has_first_stream && (
                <LivexStudioCoachmark
                  academyId={user?.academy_id}
                  label="First time?"
                  message="Tap here to start your first academy broadcast."
                />
              )}
            </div>
          </form>

          <div className="mt-10 p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-label" style={{ marginBottom: '8px' }}>What happens next</div>
            <ol className="livex-body-text space-y-1.5">
              <li>1. We mint a private broadcaster room on LiveKit Cloud</li>
              <li>2. You grant camera + mic access and click <span className="livex-body-strong">Go Live</span></li>
              <li>3. Share the public watch link with parents, fans, scouts</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // ---------- LIVE BROADCAST PANEL ----------
  const mocked = stream.mocked || !stream.broadcaster_token;

  // Reusable bits — the live broadcast view inside <LiveKitRoom>
  const TopBarInner = (
    <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
            <Radio className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {!ended && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-600 text-white text-[10px] font-black uppercase tracking-wider rounded">
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> LIVE
                </span>
              )}
              {ended && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-700 text-white text-[10px] font-black uppercase tracking-wider rounded">ENDED</span>}
              <span className="text-xs font-bold text-white capitalize">{stream.sport || 'event'}</span>
            </div>
            <h1 className="text-base font-bold text-white truncate max-w-[60vw] sm:max-w-md">{stream.title}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!mocked && !ended && (
            <BroadcasterTelemetry
              streamId={stream.id}
              onCountChange={setViewerCount}
              onPeakChange={setPeakViewers}
              currentPeak={peakViewers}
            />
          )}
          {!ended && (
            <button
              onClick={handleEnd}
              data-testid="livex-studio-end"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-rose-700 hover:bg-rose-600 border-2 border-rose-600 rounded-lg transition-all"
            >
              <StopCircle className="h-4 w-4" /> End
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const RightColumn = (
    <div className="space-y-4">
      {/* Watch link */}
      <div className="p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] rounded-xl">
        <div className="flex items-center gap-2 mb-3">
          <LinkIcon className="h-4 w-4 text-[#FF5A1F]" />
          <div className="livex-label" style={{ marginBottom: 0 }}>Public Watch Link</div>
        </div>
        <div className="livex-url-box mb-3" data-testid="livex-studio-watch-url">{watchUrl}</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleCopy}
            data-testid="livex-studio-copy"
            className="inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-bold text-white bg-[#07111F] hover:bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all"
          >
            {copied ? <><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
          </button>
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="livex-studio-open-watch"
            className="inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
          >
            <ExternalLink className="h-4 w-4" /> Open
          </a>
        </div>
      </div>

      {/* Live audience card */}
      {!mocked && !ended && (
        <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl" data-testid="livex-studio-audience">
          <div className="livex-label" style={{ marginBottom: '14px' }}>Live Audience</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-[#0c1a2e] border-2 border-[#22314F]">
              <div className="livex-detail-key mb-1">Watching now</div>
              <div className="text-2xl font-black text-white tabular-nums" data-testid="livex-studio-live-count">{viewerCount}</div>
            </div>
            <div className="p-3 rounded-lg bg-[#0c1a2e] border-2 border-[#22314F]">
              <div className="livex-detail-key mb-1 flex items-center gap-1"><TrendingUp className="h-3 w-3 text-[#FF5A1F]" /> Peak</div>
              <div className="text-2xl font-black text-white tabular-nums" data-testid="livex-studio-peak-count">{peakViewers}</div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 140A — Broadcaster score controls + sport-aware increments */}
      {!ended && (
        <BroadcasterScorePanel streamId={stream.id} initialScoreboard={stream.scoreboard} />
      )}

      {/* Phase 140A — Stream Health Badge. The LiveKit `useRoomContext` hook
          inside this component gracefully returns null when rendered outside
          <LiveKitRoom> (preview / mocked mode), so we mount it unconditionally
          and let it self-disable when there's no live room. */}
      {!mocked && !ended && (
        <StreamHealthBadge
          streamId={stream.id}
          noiseSuppressionOn={audioSettings.noiseSuppression}
        />
      )}

      {/* Phase 140B.2 — Manual highlight marker (⭐). Placed above the audio
          panel so it's the first action the broadcaster sees in the cockpit.
          Sport prop drives the order of the type picker (cricket → wicket first). */}
      {!mocked && !ended && (
        <HighlightMarkerButton streamId={stream.id} sport={stream.sport} />
      )}

      {/* Phase 140B.1 — Audio Enhancement controls (broadcaster-only).
          Toggles persist in localStorage; flipping mid-stream surfaces a
          "Restart broadcast to apply" hint inside the panel. `key={stream.id}`
          remounts the panel on each new broadcast so the snapshot used for
          drift detection captures the freshly-applied settings. */}
      {!mocked && !ended && (
        <BroadcasterAudioPanel
          key={stream.id}
          settings={audioSettings}
          setSetting={setAudioSetting}
          liveBroadcastActive
        />
      )}

      {/* Stream meta */}
      <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
        <div className="livex-label" style={{ marginBottom: '14px' }}>Stream Details</div>
        <div className="space-y-3">
          <div className="flex justify-between items-center gap-3">
            <span className="livex-detail-key">Stream ID</span>
            <span className="livex-detail-value font-mono text-xs">{stream.id.slice(0, 12)}…</span>
          </div>
          <div className="flex justify-between items-center gap-3">
            <span className="livex-detail-key">Sport</span>
            <span className="livex-detail-value capitalize">{stream.sport || '—'}</span>
          </div>
          <div className="flex justify-between items-center gap-3">
            <span className="livex-detail-key">Status</span>
            <span className={`livex-detail-value uppercase ${ended ? '' : 'livex-detail-value-live'}`}>{ended ? 'Ended' : 'Live'}</span>
          </div>
          <div className="flex justify-between items-center gap-3">
            <span className="livex-detail-key">Mode</span>
            <span className="livex-detail-value">{mocked ? 'Preview' : 'Broadcast'}</span>
          </div>
        </div>
      </div>

      {/* Tips strip */}
      <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl space-y-3">
        <div className="livex-label" style={{ marginBottom: '4px' }}>Broadcast tips</div>
        <div className="flex items-start gap-3"><Video className="h-4 w-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-bold text-white">Landscape orientation</div>
            <div className="livex-body-text" style={{ fontSize: '13px' }}>Holds the ball, scoreboard, and players in frame.</div>
          </div>
        </div>
        <div className="flex items-start gap-3"><Mic className="h-4 w-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-bold text-white">Crowd mic on</div>
            <div className="livex-body-text" style={{ fontSize: '13px' }}>Ambient sound makes the stream feel alive.</div>
          </div>
        </div>
        <div className="flex items-start gap-3"><Share2 className="h-4 w-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-bold text-white">Share early</div>
            <div className="livex-body-text" style={{ fontSize: '13px' }}>Send the watch link 5 min before kickoff for max reach.</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 text-sm font-bold flex items-start gap-2" data-testid="livex-studio-live-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}
    </div>
  );

  // Preview mode (LiveKit env vars blank) — no LiveKitRoom, simpler layout.
  if (mocked) {
    return (
      <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-studio-live">
        {TopBarInner}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-video bg-black rounded-xl overflow-hidden border-2 border-[#22314F] relative" data-testid="livex-studio-video">
              <div className="absolute inset-0 grid place-items-center p-8 text-center">
                <div>
                  <div className="h-16 w-16 rounded-2xl mx-auto mb-4 grid place-items-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                    <Video className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-lg font-black text-white mb-2">Preview Mode</div>
                  <p className="livex-body-text max-w-md mx-auto">
                    LiveKit credentials are not configured yet. The stream URL and watch flow are fully wired —
                    add <span className="font-mono text-[#FF5A1F]">LIVEKIT_API_KEY / SECRET / WS_URL</span> to <span className="font-mono text-[#FF5A1F]">backend/.env</span> to enable real broadcasting.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl text-center livex-body-text">
              Chat opens automatically when LiveKit is configured.
            </div>
          </div>
          {RightColumn}
        </div>
      </div>
    );
  }

  // Real broadcast mode — wrap everything in <LiveKitRoom> so chat + viewer badge get context.
  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-studio-live">
      <LiveKitRoom
        serverUrl={stream.server_url}
        token={stream.broadcaster_token}
        connect
        video
        audio={audioConstraints}
        data-lk-theme="default"
      >
        <RoomAudioRenderer />
        {/* Phase 140A-replay-fix — Fires /start-recording once the broadcaster's
            first track is published, the only reliable signal that the LiveKit
            room actually exists. Renders nothing. */}
        <EgressKickoff streamId={stream.id} />
        {TopBarInner}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-video bg-black rounded-xl overflow-hidden border-2 border-[#22314F] relative" data-testid="livex-studio-video">
              <BroadcasterGrid />
              <div className="absolute bottom-0 left-0 right-0 livex-controls-shell">
                <ControlBar variation="verbose" controls={{ microphone: true, camera: true, screenShare: false, chat: false, leave: false }} />
              </div>
            </div>
            {/* Phase 131C — Highlight markers (foundational event substrate) */}
            <LivexHighlightPanel
              streamId={stream.id}
              apiClient={api}
              apiPathBase={`/livex/streams/${stream.id}`}
            />
            {/* Broadcaster chat panel — full moderation controls */}
            <div className="min-h-[420px]">
              <LivexChatPanel
                stream={stream}
                isBroadcaster
                viewerName={stream.host_name || 'Host'}
                onStreamUpdate={handleStreamUpdate}
                apiClient={api}
                apiPathBase={`/livex/streams/${stream.id}`}
              />
            </div>
          </div>
          {RightColumn}
        </div>
      </LiveKitRoom>
    </div>
  );
}
